/** Import flow (design spec §9): file → preview (findings, base-match
 * banner, diff, stale-draft warning) → confirm publish. The file is read
 * client-side and POSTed as JSON — no multipart. */
import { useState } from 'react'
import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '@/components/ui'
import {
  useWorkflowVersionImportApply,
  useWorkflowVersionImportPreview,
} from '@/hooks/useWorkflowVersions'
import type { ImportPreview, VersionImportEnvelope } from '@/types/workflowVersions'
import { refusalMessage } from './refusalMessage'

export function ImportVersionDialog({
  workflowId,
  onClose,
}: {
  workflowId: string
  onClose: () => void
}) {
  const [envelope, setEnvelope] = useState<VersionImportEnvelope | null>(null)
  const [parseError, setParseError] = useState(false)
  const [applied, setApplied] = useState<number | 'no_op' | null>(null)
  const preview = useWorkflowVersionImportPreview(workflowId)
  const apply = useWorkflowVersionImportApply(workflowId)

  async function onFile(file: File | undefined) {
    setParseError(false)
    setEnvelope(null)
    preview.reset()
    apply.reset()
    if (!file) return
    try {
      const parsed = JSON.parse(await file.text()) as VersionImportEnvelope
      setEnvelope(parsed)
      preview.mutate(parsed)
    } catch {
      setParseError(true)
    }
  }

  const p: ImportPreview | undefined = preview.data

  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose() }}>
      <ModalContent size="sm" showClose={false}>
        <ModalHeader><ModalTitle>Import a workflow version</ModalTitle></ModalHeader>
        <ModalBody className="space-y-3">
          {applied === null && (
            <>
              <label className="block text-sm">
                Version export file
                <input
                  type="file"
                  accept=".json,application/json"
                  className="mt-1 block w-full text-sm"
                  onChange={(e) => onFile(e.target.files?.[0])}
                />
              </label>
              {parseError && (
                <p className="text-sm text-destructive">
                  Couldn't read that file — it isn't a workflow version export.
                </p>
              )}
              {apply.isError && (
                <p className="text-sm text-destructive">
                  {refusalMessage(apply.error) ?? 'Import failed — re-check the preview.'}
                </p>
              )}
              {preview.isPending && (
                <p className="text-sm text-muted-foreground">Checking file…</p>
              )}
              {preview.isError && (
                <p className="text-sm text-destructive">
                  Couldn't check this file with the server — try again.
                </p>
              )}
              {p && (
                <div className="space-y-2 text-sm">
                  {p.source_label && (
                    <p className="text-muted-foreground">
                      From {p.source_label}
                      {p.source_version_number != null && <> v{p.source_version_number}</>}
                    </p>
                  )}
                  {p.base_match === 'match' && (
                    <p className="rounded bg-success/10 px-2 py-1 text-success">
                      Base matches this environment.
                    </p>
                  )}
                  {p.base_match === 'mismatch' && (
                    <p className="rounded bg-warning/10 px-2 py-1 text-warning">
                      Exported against a different base — review the changes carefully.
                    </p>
                  )}
                  {p.base_match === 'unknown' && (
                    <p className="rounded bg-warning/10 px-2 py-1 text-warning">
                      Couldn't verify which base this was exported against — review the changes
                      carefully.
                    </p>
                  )}
                  {p.draft_differs && (
                    <p className="rounded bg-warning/10 px-2 py-1 text-warning">
                      This workflow has unpublished draft edits; publishing that draft later
                      will override this import.
                    </p>
                  )}
                  {!p.valid &&
                    p.findings.map((f) => (
                      <p key={`${f.path}:${f.code}`} className="text-destructive">
                        {f.path}: {f.message}
                      </p>
                    ))}
                  {p.valid && p.no_op && (
                    <p className="text-muted-foreground">No changes — already up to date.</p>
                  )}
                  {p.valid && (
                    <ul className="space-y-0.5">
                      {p.diff.overridden.map((ref) => <li key={ref}>~ {ref}</li>)}
                      {p.diff.added.map((ref) => <li key={ref}>+ {ref}</li>)}
                      {p.diff.disabled.map((ref) => <li key={ref}>− {ref}</li>)}
                      {p.diff.settings.map((ref) => <li key={ref}>⚙ {ref}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </>
          )}
          {applied === 'no_op' && <p className="text-sm">Nothing to import — already up to date.</p>}
          {typeof applied === 'number' && (
            <p className="text-sm">Imported and published — this workflow is now v{applied}.</p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={onClose}>
            {applied === null ? 'Cancel' : 'Close'}
          </Button>
          {applied === null && p?.valid && envelope && (
            <Button
              disabled={apply.isPending}
              isLoading={apply.isPending}
              onClick={() =>
                apply.mutate(envelope, {
                  onSuccess: (result) =>
                    setApplied(result.no_op ? 'no_op' : result.version?.version_number ?? 'no_op'),
                })
              }
            >
              {apply.isPending ? 'Importing…' : 'Import & publish'}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
